/**
 * Going into a page and back out (ADR-050). A page opened from My Stay or Help slides in from
 * the right and Back slides it away, the way a phone's own apps feel. React Router runs the
 * browser's View Transitions (where there are none, the page just appears; guest.css slides it in
 * instead), and `data-nav` on <html> tells guest.css which way to slide. The top bar and the tabs
 * have their own transition names, so they stay put while the page moves.
 */
import { useEffect, useRef, type AnchorHTMLAttributes } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

type Direction = 'forward' | 'back' | 'none';

const canTransition = typeof document !== 'undefined' && 'startViewTransition' in document;
if (typeof document !== 'undefined' && !canTransition) document.documentElement.classList.add('no-vt');

export function setDirection(d: Direction): void {
  document.documentElement.dataset.nav = d;
}

/** A link into a page: it slides in from the right. `replace` swaps the current page for it
 *  (Next, and links out of the Help sheet), so Back doesn't step through each one. */
export function PushLink({ to, onClick, replace, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }) {
  return (
    <Link
      to={to}
      replace={replace}
      viewTransition
      onClick={(e) => {
        setDirection('forward');
        onClick?.(e);
      }}
      {...rest}
    />
  );
}

/**
 * Back from a page: to wherever the guest came from (scroll position and all), or, when the page
 * was opened straight from a link in a message, to `fallback`.
 */
export function useBack(fallback: string): () => void {
  const navigate = useNavigate();
  const { key } = useLocation();
  return () => {
    setDirection('back');
    if (key !== 'default') void navigate(-1);
    else void navigate(fallback, { replace: true, viewTransition: true });
  };
}

function historyIndex(state: unknown): number {
  const idx = (state as { idx?: unknown } | null)?.idx;
  return typeof idx === 'number' ? idx : 0;
}

/**
 * The browser's own Back and Forward slide the right way. When the phone already animated the
 * swipe itself (Safari's edge swipe sets `hasUAVisualTransition`), there's no second slide.
 */
export function useNavDirection(): void {
  const location = useLocation();
  const idx = useRef(0);
  useEffect(() => {
    idx.current = historyIndex(window.history.state);
  }, [location]);
  useEffect(() => {
    const onPop = (e: PopStateEvent & { hasUAVisualTransition?: boolean }) => {
      const next = historyIndex(e.state);
      setDirection(e.hasUAVisualTransition ? 'none' : next < idx.current ? 'back' : 'forward');
      idx.current = next;
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
}

/**
 * On the home screen app there's no browser swipe to go back, so a swipe in from the left edge
 * of a page does it (ADR-050). In the browser the browser's own swipe already does.
 */
export function useEdgeSwipeBack(back: () => void): void {
  const backRef = useRef(back);
  useEffect(() => {
    backRef.current = back;
  });
  useEffect(() => {
    if (!window.matchMedia?.('(display-mode: standalone)').matches) return;
    let start: { x: number; y: number } | null = null;
    const down = (e: TouchEvent) => {
      const t = e.touches[0];
      start = t && e.touches.length === 1 && t.clientX < 24 ? { x: t.clientX, y: t.clientY } : null;
    };
    const up = (e: TouchEvent) => {
      const t = e.changedTouches[0];
      if (start && t && t.clientX - start.x > 80 && Math.abs(t.clientY - start.y) < 60) backRef.current();
      start = null;
    };
    window.addEventListener('touchstart', down, { passive: true });
    window.addEventListener('touchend', up, { passive: true });
    return () => {
      window.removeEventListener('touchstart', down);
      window.removeEventListener('touchend', up);
    };
  }, []);
}
